import { CheckCircle2, Rocket, Smartphone, Globe, Server, ShieldCheck, MessageCircle, ExternalLink, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import LatestVideosSection from "@/components/public/LatestVideosSection";
import StoreBadges from "@/components/brand/StoreBadges";
import Hero3DScene from "@/components/public/Hero3DScene";
import FlagshipCard from "@/components/public/FlagshipCard";
import { FLAGSHIPS, STATS } from "@/lib/brand";

const WHATSAPP_NUMBER = "963992367582";
const WHATSAPP_PROJECT_MESSAGE = "مرحباً، أريد بدء مشروع جديد";
const WHATSAPP_CONSULT_MESSAGE = "مرحباً، أريد الحصول على استشارة مجانية";


const Home = () => {
    const whatsappProjectLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_PROJECT_MESSAGE)}`;
    const whatsappConsultLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_CONSULT_MESSAGE)}`;

    const services = [
        {
            icon: <Smartphone className="w-8 h-8" />,
            title: "تطبيقات الموبايل",
            desc: "تطبيقات iOS و Android بأعلى معايير الجودة.",
            size: "col-span-1 row-span-1",
        },
        {
            icon: <Globe className="w-8 h-8" />,
            title: "تطوير الويب",
            desc: "مواقع سريعة، متجاوبة ومحسنة لمحركات البحث.",
            size: "col-span-1 md:col-span-2 row-span-1",
        },
        {
            icon: <Server className="w-8 h-8" />,
            title: "أنظمة المؤسسات",
            desc: "حلول البرمجيات المخصصة (ERP/CRM) لإدارة أعمالك.",
            size: "col-span-1 md:col-span-2 row-span-1",
        },
        {
            icon: <ShieldCheck className="w-8 h-8" />,
            title: "الأمان والحماية",
            desc: "نضمن أمن بياناتك واستقرار نظامك على مدار الساعة.",
            size: "col-span-1 row-span-1",
        },
    ];

    const stats = [
        { number: STATS.projects, label: "مشروع متكامل" },
        { number: STATS.clients, label: "عميل حول العالم" },
        { number: STATS.satisfaction, label: "نسبة الرضا" },
        { number: "24/7", label: "دعم فني حي" },
    ];

    return (
        <div className="flex flex-col">
            {/* Hero Section - LCP Optimized: No animation delays on critical content */}
            <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20">
                <div className="container mx-auto px-6 relative z-10">
                    <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
                        <div className="text-center lg:text-start">
                            {/* Badge with CSS animation instead of JS */}
                            <div
                                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-white/60 mb-8 animate-fade-in"
                            >
                                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                                مستقبل أعمالك يبدأ من هنا
                            </div>

                            {/* H1 - LCP Element: Visible immediately, no opacity:0 initial state */}
                            <h1
                                className="text-5xl md:text-7xl xl:text-8xl font-black text-white mb-8 tracking-normal animate-fade-in"
                                style={{ lineHeight: '1.6', animationDelay: '0.1s' }}
                            >
                                نصمم الحلول <br />
                                <span className="text-muted-foreground">الرقمية للمستقبل.</span>
                            </h1>

                            <p
                                className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-12 leading-relaxed font-medium animate-fade-in"
                                style={{ animationDelay: '0.2s' }}
                            >
                                في TRBD، ندمج الإبداع بالتكنولوجيا لنحول طموحاتك إلى منتجات رقمية استثنائية.
                                جودة عالمية، وأداء لا يضاهى، بأسعار مدروسة بعناية.
                            </p>

                            <div
                                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 animate-fade-in"
                                style={{ animationDelay: '0.3s' }}
                            >
                                <a href={whatsappProjectLink} target="_blank" rel="noopener noreferrer">
                                    <Button size="lg" className="h-14 px-8 text-lg rounded-full bg-[#128C7E] text-white hover:bg-[#075E54] font-bold shadow-tech transition-all gap-2">
                                        <MessageCircle size={22} />
                                        تواصل عبر واتساب
                                    </Button>
                                </a>

                                <Button
                                    onClick={() => window.dispatchEvent(new CustomEvent('open-chat'))}
                                    size="lg"
                                    className="h-14 px-8 text-lg rounded-full bg-gradient-to-r from-primary to-secondary text-white hover:shadow-lg hover:shadow-primary/30 font-bold shadow-tech transition-all gap-2"
                                >
                                    <Rocket size={22} />
                                    ابدأ مشروعك الآن
                                </Button>

                                <Link to="/about">
                                    <Button variant="outline" size="lg" className="h-14 px-8 text-lg rounded-full border-white/10 hover:bg-white/5 text-white font-bold">
                                        تعرف علينا
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        {/* 3D Scene — phone + desktop floating in */}
                        <div className="hidden lg:block">
                            <Hero3DScene />
                        </div>
                    </div>
                </div>


                {/* Decorative Elements */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none overflow-hidden">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px]" />
                </div>
            </section>

            {/* Stats Section - Fixed heading hierarchy: removed h3, using p + strong */}
            <section className="py-20 border-y border-white/5 bg-white/[0.01]">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
                        {stats.map((stat, i) => (
                            <div key={i} className="text-center group">
                                <p className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tighter group-hover:text-primary transition-colors">
                                    {stat.number}
                                </p>
                                <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest leading-none">
                                    {stat.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Bento Grid */}
            <section className="py-32">
                <div className="container mx-auto px-6">
                    <div className="max-w-3xl mb-20">
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-normal" style={{ lineHeight: '1.6' }}>
                            خدماتنا <br />
                            <span className="text-muted-foreground">تغطي كل احتياجاتك.</span>
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                            نحن لا نبني مجرد برمجيات، بل نصنع أدوات لنمو أعمالك بشكل مستدام.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[250px]">
                        {services.map((service, i) => (
                            <div
                                key={i}
                                className={`bento-card p-10 flex flex-col justify-end group ${service.size}`}
                            >
                                <div className="p-4 rounded-xl bg-white/5 w-fit mb-6 text-white group-hover:scale-110 group-hover:bg-primary transition-all duration-300">
                                    {service.icon}
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{service.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                                        {service.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Flagship Products */}
            <section className="py-32 bg-white/[0.01] border-y border-white/5">
                <div className="container mx-auto px-6">
                    <div className="max-w-3xl mb-16">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary mb-6">
                            <Users className="w-3 h-3" />
                            منتجاتنا الرائدة
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-normal" style={{ lineHeight: '1.6' }}>
                            علامات تجارية <br />
                            <span className="text-muted-foreground">صنعناها من الصفر.</span>
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                            {STATS.projects} مشروع برمجي، {STATS.clients} عميل حول العالم. من أبرز المنصات التي نمتلكها ونديرها:
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 [perspective:1400px]">
                        {FLAGSHIPS.map((f) => (
                            <FlagshipCard key={f.key} flagship={f} />
                        ))}
                    </div>

                    <div className="bento-card p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div>
                            <p className="text-xs font-black text-white/40 uppercase tracking-widest mb-2">كمان عندنا</p>
                            <p className="text-xl md:text-2xl font-bold text-white">تطبيقات منشورة على Google Play و App Store</p>
                        </div>
                        <StoreBadges />
                    </div>

                    <div className="mt-10 text-center">
                        <Link to="/portfolio">
                            <Button size="lg" className="h-14 px-10 text-base rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 font-bold gap-2">
                                استعرض كل مشاريعنا ({STATS.projects})
                                <ExternalLink className="w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Value Proposition */}
            <section className="py-32 bg-white/[0.01]">
                <div className="container mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-20 items-center">
                        <div>
                            <h2 className="text-4xl md:text-7xl font-black text-white mb-10 tracking-normal" style={{ lineHeight: '1.6' }}>
                                جودة عالمية <br />
                                من قلب <span className="text-primary italic">الشام.</span>
                            </h2>
                            <p className="text-xl text-muted-foreground leading-relaxed font-medium mb-12">
                                نحن في TRBD نفتخر بتقديم خدماتنا من دمشق - المزة، مما يمكننا من تقديم جودة برمجية تنافسية عالمياً بأسعار محلية ذكية.
                            </p>
                            <div className="space-y-6">
                                {[
                                    "فريق هندسي متخصص ذو خبرة واسعة",
                                    "التزام تام بالمواعيد والجودة",
                                    "دعم فني متواصل وحلول مرنة",
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-4 group">
                                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                                            <CheckCircle2 size={14} />
                                        </div>
                                        <span className="text-lg font-bold text-white/90 group-hover:text-white transition-colors">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative">
                            <div className="aspect-square rounded-[3rem] bg-gradient-to-br from-primary/10 to-transparent border border-white/5 flex items-center justify-center overflow-hidden">
                                <div className="absolute inset-0 bg-grid opacity-10" />
                                {/* Replaced motion.div with CSS animation */}
                                <div
                                    className="relative z-10 w-48 h-48 bg-white/5 backdrop-blur-3xl rounded-3xl border border-white/10 flex items-center justify-center shadow-2xl animate-float"
                                >
                                    <Rocket size={80} className="text-white opacity-20" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Latest YouTube Videos */}
            <LatestVideosSection />

            {/* CTA Section */}
            <section className="py-40">
                <div className="container mx-auto px-6">
                    <div className="bento-card p-16 md:p-32 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 blur-[100px] -mr-48 -mt-48" />
                        <div className="relative z-10">
                            <h2 className="text-5xl md:text-8xl font-black text-white mb-10 tracking-normal" style={{ lineHeight: '1.6' }}>
                                هل أنت مستعد للقفزة؟
                            </h2>
                            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-16 leading-relaxed font-medium">
                                تواصل معنا الآن عبر واتساب للحصول على استشارة مجانية وعرض سعر مخصص لمشروعك القادم.
                            </p>
                            <a href={whatsappConsultLink} target="_blank" rel="noopener noreferrer">
                                <Button size="lg" className="h-20 px-16 text-2xl rounded-full bg-[#128C7E] text-white hover:bg-[#075E54] font-black shadow-tech transition-all gap-4">
                                    <MessageCircle size={32} />
                                    تواصل عبر واتساب
                                </Button>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
